import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Fuel, Users, Zap, Gauge } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function VehicleCard({ vehicle, index = 0, onEnquire }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="group relative overflow-hidden rounded-3xl glass glass-hover tilt-3d shine-sweep"
      data-testid={`vehicle-card-${vehicle.slug}`}
    >
      {/* Accent glow */}
      <div className={`absolute inset-0 bg-gradient-to-br ${vehicle.accent} opacity-40 pointer-events-none`} />

      <div className="relative p-6 md:p-8">
        {/* Image */}
        <div className="relative aspect-[16/10] rounded-2xl overflow-hidden bg-gradient-to-br from-slate-900 to-slate-950 mb-6">
          <img
            src={vehicle.image}
            alt={vehicle.name}
            className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-110"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
          {vehicle.isEV && (
            <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-cyan-500/20 backdrop-blur-md border border-cyan-400/40 text-cyan-300 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
              <Zap className="w-3 h-3" /> Electric
            </span>
          )}
          <span className="absolute bottom-4 left-4 bg-yellow-400 text-black text-[10px] font-bold tracking-wider px-2 py-1 rounded font-mono-brand">
            T-PERMIT
          </span>
        </div>

        {/* Content */}
        <div>
          <div className="flex items-start justify-between gap-4 mb-2">
            <div>
              <h3 className="font-display text-xl md:text-2xl font-bold text-white leading-tight">
                {vehicle.name}
              </h3>
              <p className="text-sm text-red-400 mt-1">{vehicle.tagline}</p>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[10px] uppercase tracking-widest text-slate-400">From</div>
              <div className="font-display text-lg font-bold text-white">{vehicle.priceFrom}</div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-2 my-5">
            <Stat icon={Fuel} label="Fuel" value={vehicle.fuel} />
            <Stat icon={Users} label="Seats" value={`${vehicle.seats}`} />
            <Stat icon={Gauge} label="Mileage" value={vehicle.mileage} />
          </div>

          {/* Ideal for */}
          <div className="flex flex-wrap gap-2 mb-5">
            {vehicle.idealFor.map((tag) => (
              <span
                key={tag}
                className="text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full border border-white/10 bg-white/5 text-slate-300"
              >
                {tag}
              </span>
            ))}
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Button
              onClick={() => onEnquire?.(vehicle.name)}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-full"
              data-testid={`vehicle-enquire-${vehicle.slug}`}
            >
              Enquire Now
            </Button>
            <Link
              to={`/vehicles/${vehicle.slug}`}
              className="w-11 h-11 shrink-0 rounded-full border border-white/15 flex items-center justify-center text-white hover:bg-white/10 hover:border-red-500 transition-colors"
              aria-label={`View details for ${vehicle.name}`}
              data-testid={`vehicle-details-${vehicle.slug}`}
            >
              <ArrowUpRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl bg-white/5 border border-white/5 px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-slate-400 mb-1">
        <Icon className="w-3.5 h-3.5" />
        <span className="text-[10px] uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-sm font-semibold text-white">{value}</div>
    </div>
  );
}
